import {Show} from 'platform/foundation';

import {uniq} from 'ramda';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CALENDAR_CONSTANTS} from '../constants/calendarConstants';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {getRentalStateAppearance} from '../utils/getRentalStateAppearance';

interface RentalCalendarDayChipProps extends RequiredTestIdProps {
  day: Date;
  columnIndex: number;
  laneIndex: number;
  events: RentalResourceCalendarEvent[];
  onDayDrillDown?: (day: Date) => void;
}

export function RentalCalendarDayChip(props: RentalCalendarDayChipProps) {
  const stateColors = uniq(
    props.events.map((event) => getRentalStateAppearance(event.rentalOrder?.state).cssColor)
  );

  const customerNames = props.events
    .map((event) => event.rentalOrder?.customerName ?? EMPTY_PLACEHOLDER)
    .join(' · ');

  return (
    <button
      type="button"
      className="absolute flex cursor-pointer items-center justify-center gap-1 rounded-small hover:shadow-elevation-1"
      // `resetCss` zeroes button background and border widths from an unlayered sheet, which beats
      // Tailwind's utilities layer — inline styles are the only thing that outranks it
      style={{
        backgroundColor: CHIP_BACKGROUND,
        border: CHIP_BORDER,
        left: props.columnIndex * CALENDAR_CONSTANTS.COLUMN_WIDTH + CHIP_INSET,
        width: CALENDAR_CONSTANTS.COLUMN_WIDTH - CHIP_INSET * 2,
        top:
          props.laneIndex * CALENDAR_CONSTANTS.ROW_HEIGHT +
          Math.floor(CALENDAR_CONSTANTS.ROW_HEIGHT * CHIP_TOP_RATIO),
        height: Math.floor(CALENDAR_CONSTANTS.ROW_HEIGHT * CHIP_HEIGHT_RATIO),
        zIndex: CHIP_Z_INDEX,
      }}
      title={customerNames}
      aria-label={customerNames}
      data-testid={suffixTestId(`dayChip-${props.columnIndex}`, props)}
      onClick={(mouseEvent) => {
        // The track behind creates a rental on click — drilling into the day must not reach it
        mouseEvent.stopPropagation();
        props.onDayDrillDown?.(props.day);
      }}
    >
      <span className="text-xs font-bold text-primary">{props.events.length}</span>
      <span className="flex items-center gap-0.5">
        {stateColors.slice(0, MAXIMUM_STATE_DOTS).map((color) => (
          <span key={color} className="size-1.5 rounded-full" style={{backgroundColor: color}} />
        ))}
        <Show when={stateColors.length > MAXIMUM_STATE_DOTS}>
          <span className="text-[0.625rem] text-secondary">+</span>
        </Show>
      </span>
    </button>
  );
}

const CHIP_BACKGROUND = 'var(--color-palettes-neutral-20-100)';
const CHIP_BORDER = '1px dashed var(--color-palettes-neutral-50-100)';

/** Leaves the day's grid lines visible on both sides of the chip */
const CHIP_INSET = 4;
const CHIP_TOP_RATIO = 0.1;
const CHIP_HEIGHT_RATIO = 0.8;

/** Event bars use `eventIndex + 1`, so the chip has to sit above them to stay clickable */
const CHIP_Z_INDEX = 11;

/** Beyond this the dots stop reading as distinct states */
const MAXIMUM_STATE_DOTS = 4;
